// =========================================================
// ONYX TRIAGE & DIAGNOSTIC ENGINE (TRUTHFUL & RESILIENT)
// =========================================================

export function evaluateNextOptimalAssessment(history = [], userDisplayName = '') {
    const firstName = String(userDisplayName || 'there').split(' ')[0];

    // 1. Sanitize and extract valid completed records
    if (!Array.isArray(history) || history.length === 0) {
        return {
            status: 'cold_start',
            greetingName: firstName,
            message: "Welcome! To establish your active baseline, start by mapping your cognitive framework or taking a workplace strain check-in.",
            recommendedPath: 'sapphire',
            recommendedLabel: 'Launch Psychometric Matrix'
        };
    }

    // Filter valid records containing scores or chart data
    const validHistory = history.filter(item => item && item.toolId && (item.scores || item.chartData));

    // Deduplicate to get the latest record per tool
    const latestByTool = {};
    validHistory.forEach(item => {
        const id = item.toolId;
        if (!latestByTool[id] || new Date(item.date) > new Date(latestByTool[id].date)) {
            latestByTool[id] = item;
        }
    });

    const completedTools = Object.keys(latestByTool);
    const count = completedTools.length;

    // 2. HONESTY BARRIER: Single survey completed
    if (count === 1) {
        const singleToolId = completedTools[0];
        const singleRecord = latestByTool[singleToolId];
        const toolTitle = singleRecord.toolName || singleRecord.tool || 'Assessment';

        // Suggest the complementary pairing tool for the single completed assessment
        const complementaryPairs = {
            copsoq: { id: 'dass21', name: 'DASS-21 (Clinical Stress)' },
            dass21: { id: 'copsoq', name: 'COPSOQ III (Workplace Demands)' },
            obi16: { id: 'copsoq', name: 'COPSOQ III (Workplace Demands)' },
            sds6: { id: 'uwes17', name: 'UWES-17 (Work Engagement)' },
            uwes17: { id: 'obi16', name: 'OBI-16 (Burnout Index)' },
            disc: { id: 'copsoq', name: 'COPSOQ III (Workplace Demands)' },
            sapphire: { id: 'copsoq', name: 'COPSOQ III (Workplace Demands)' },
            archetype: { id: 'dass21', name: 'DASS-21 (Clinical Stress)' },
            fivefactor: { id: 'obi16', name: 'OBI-16 (Burnout Index)' },
            workinggenius: { id: 'uwes17', name: 'UWES-17 (Work Engagement)' }
        };

        const pair = complementaryPairs[singleToolId] || { id: 'copsoq', name: 'COPSOQ III (Workplace Demands)' };

        return {
            status: 'insufficient_data',
            greetingName: firstName,
            message: `You have completed 1 assessment (${toolTitle}). While this provides a helpful initial snapshot, cross-vector analysis requires at least 2 distinct data points to form a concrete assessment without making speculative assumptions. Run ${pair.name} next to complete your baseline.`,
            recommendedPath: pair.id,
            recommendedLabel: `Launch ${pair.name}`
        };
    }

    // 3. MULTI-VECTOR CROSS-ANALYSIS (>= 2 Surveys Completed)
    const has = (id) => !!latestByTool[id];
    const get = (id) => latestByTool[id];

    const copsoq = get('copsoq');
    const dass = get('dass21');
    const obi = get('obi16');
    const sds = get('sds6');
    const sapphire = get('sapphire');
    const disc = get('disc');
    const fivefactor = get('fivefactor');
    const genius = get('workinggenius');

    // --- CROSS-VECTOR DETECTIONS ---

    // A. Perfectionism Multiplier: High Precision OS (High C / INTJ / Conscientious) + High Workplace Load
    const isHighPrecisionOS = 
        (disc && disc.scores?.most?.C > 8) || 
        (sapphire && String(sapphire.scores?.mbti || '').includes('TJ')) ||
        (fivefactor && fivefactor.scores?.[3] > 38);

    if (isHighPrecisionOS && copsoq && copsoq.scores?.quantitativeDemands > 60) {
        if (!has('obi16')) {
            return {
                status: 'high_strain',
                greetingName: firstName,
                message: "Your profile indicates a high-precision cognitive style operating under elevated quantitative workload. High internal standards can double perceived task strain. Run OBI-16 to check for chronic exhaustion.",
                recommendedPath: 'obi16',
                recommendedLabel: 'Launch OBI-16 (Burnout Index)'
            };
        }
    }

    // B. Relational Saturation: High Empathy OS (High S / High Agreeable) + High Emotional Demands
    const isHighEmpathyOS = 
        (disc && (disc.scores?.most?.S > 8 || disc.scores?.most?.I > 8)) ||
        (fivefactor && fivefactor.scores?.[2] > 38);

    if (isHighEmpathyOS && copsoq && copsoq.scores?.emotionalDemands > 60) {
        if (!has('dass21')) {
            return {
                status: 'high_strain',
                greetingName: firstName,
                message: "Your empathetic behavioral style combined with high workplace emotional demands creates an environment for silent relational fatigue. Run DASS-21 to measure internal anxiety and stress levels.",
                recommendedPath: 'dass21',
                recommendedLabel: 'Launch DASS-21 (Clinical Stress)'
            };
        }
    }

    // C. Clinical Strain Escalation: COPSOQ III / DASS-21 / OBI-16
    const hasHighDass = dass && (dass.scores?.depression >= 21 || dass.scores?.anxiety >= 15 || dass.scores?.stress >= 26);
    const hasHighObi = obi && (obi.scores?.finalOdbiScore >= 2.68);
    const hasHighCopsoq = copsoq && (copsoq.scores?.quantitativeDemands > 60 || copsoq.scores?.workPace > 60);

    if ((hasHighDass || hasHighObi || hasHighCopsoq) && !has('workinggenius')) {
        return {
            status: 'high_strain',
            greetingName: firstName,
            message: "Elevated strain detected across your clinical baselines. Mapping your Working Genius matrix will identify whether task drudgery outside your natural gifts is accelerating your exhaustion.",
            recommendedPath: 'workinggenius',
            recommendedLabel: 'Launch Working Genius Matrix'
        };
    }

    // D. Spiritual Dryness -> Work Engagement
    if (sds && sds.scores?.average >= 3.0 && !has('uwes17')) {
        return {
            status: 'high_strain',
            greetingName: firstName,
            message: "Spiritual dryness flagged on your SDS-6 check-in. Complete UWES-17 to measure if this is draining your workplace vigor and task absorption.",
            recommendedPath: 'uwes17',
            recommendedLabel: 'Launch UWES-17 (Work Engagement)'
        };
    }

    // E. Systemic Baseline Completion (Filling missing primary tools)
    const primarySuite = [
        { id: 'sapphire', name: 'Psychometric Matrix' },
        { id: 'copsoq', name: 'COPSOQ III (Workplace Demands)' },
        { id: 'dass21', name: 'DASS-21 (Clinical Stress)' },
        { id: 'obi16', name: 'OBI-16 (Burnout Index)' },
        { id: 'uwes17', name: 'UWES-17 (Work Engagement)' }
    ];

    const missingPrimary = primarySuite.find(item => !has(item.id));
    if (missingPrimary) {
        return {
            status: 'building_baseline',
            greetingName: firstName,
            message: `Your active baseline is expanding effectively. Complete ${missingPrimary.name} to unlock full cross-vector intelligence on your JD-R Balance radar.`,
            recommendedPath: missingPrimary.id,
            recommendedLabel: `Launch ${missingPrimary.name}`
        };
    }

    // F. Fully Calibrated State
    return {
        status: 'balanced',
        greetingName: firstName,
        message: "All primary clinical and behavioral baselines are fully calibrated and up to date! Explore a reflective practice tool to prepare for your next supervision session.",
        recommendedPath: 'focuscards',
        recommendedLabel: 'Launch Focus Cards'
    };
}